import { SideNavigationProps } from '@cloudscape-design/components';
import { NAV_GRAPH_ROOT_NODE_ID } from 'src/components/providers/NavGraphProvider';
import { ENGLISH_LANGUAGE_NAME } from 'src/constants';
import { ContentObjects } from 'src/types/contentObject';
import { ArticleDetails } from 'src/types/learningDetails';
import { NavGraph, NavGraphItem } from 'src/types/navGraph';
import { PreferenceLocation, PreferenceSelection } from 'src/types/preferences';
import { FilterItem as HiearchyFilterItem } from 'src/components/HierarchyFilters';

const LANGUAGE_NAME_FILTER_VALUE_MAP: Record<
    PreferenceLocation['name'],
    string
> = {
    Arabic: 'Arabic',
    Czech: 'Czech',
    'English (US)': 'English',
    'French (Canada)': 'Frenchcanadian',
    'French (France)': 'French',
    German: 'German',
    Italian: 'Italian',
    Japanese: 'Japanese',
    Polish: 'Polish',
    Spanish: 'Spanish',
    Portuguese: 'Portuguese',
};

export const getFilteredNavGraph = (
    fullNavGraph: NavGraph,
    selectedUserPreferences: PreferenceSelection,
    shouldFilterDocuments: boolean
) => {
    if (fullNavGraph) {
        const filterDocumentsByLanguage = (node: NavGraphItem) => {
            if (node.documents) {
                const filteredDocs: { [key: string]: any } = {};
                Object.entries(node.documents).forEach(([key, doc]) => {
                    const docLanguage = (doc as any).languages;
                    const userLanguage = selectedUserPreferences.language.name;

                    const isMatch =
                        userLanguage === 'English (US)'
                            ? ['English', 'English (United States)'].includes(
                                  docLanguage
                              )
                            : LANGUAGE_NAME_FILTER_VALUE_MAP[userLanguage] ===
                              docLanguage;

                    const includeEnglish =
                        shouldFilterDocuments &&
                        ENGLISH_LANGUAGE_NAME.includes(docLanguage);

                    if (isMatch || includeEnglish) {
                        filteredDocs[key] = doc;
                    }
                });
                node.documents = filteredDocs;
            }

            if (node.children) {
                Object.values(node.children).forEach(filterDocumentsByLanguage);
            }
        };

        Object.values(fullNavGraph).forEach(filterDocumentsByLanguage);
    }
};

export const findDuplicates = (
    contentObjects: ContentObjects[],
    seen = new Set()
): string[] => {
    const duplicates: string[] = [];

    contentObjects.forEach((contentObject) => {
        const key = contentObject.lcmsXmlGUID;
        if (seen.has(key)) {
            duplicates.push(contentObject.lcmsXmlGUID);
        } else if (!contentObject.topics?.length) {
            seen.add(key);
        }

        if (contentObject.topics?.length) {
            duplicates.push(...findDuplicates(contentObject.topics, seen));
        }
    });

    return duplicates;
};

export const buildDetailsSideNav = (
    contentObjects: ContentObjects[],
    duplicates: Set<string>,
    isChild = false
): SideNavigationProps.Item[] => {
    return contentObjects.map((co) => {
        const sideNavSection =
            isChild && !co.topics?.length
                ? {
                      text: co.name,
                      href: `#/${co.lcmsXmlGUID}/${encodeURIComponent(
                          `${
                              duplicates.has(co.lcmsXmlGUID)
                                  ? `${co.id}-${co.name}`
                                  : co.name
                          }`
                      )}`,
                      type: co.topics?.length
                          ? 'expandable-link-group'
                          : 'link',
                      items: co.topics?.length
                          ? buildDetailsSideNav(co.topics, duplicates, true)
                          : undefined,
                  }
                : {
                      text: co.name,
                      type: co.topics?.length
                          ? 'expandable-link-group'
                          : 'link',
                      items: co.topics?.length
                          ? buildDetailsSideNav(co.topics, duplicates, true)
                          : undefined,
                  };
        if (!sideNavSection.items || !sideNavSection.items.length) {
            delete sideNavSection.items;
        }

        return sideNavSection;
    }) as SideNavigationProps.Item[];
};

export const buildHrefMap = (
    contentObjects: ContentObjects[],
    duplicates: Set<string>
): Map<string, string> => {
    const hrefArray: string[] = [];
    const hrefs = contentObjects.map((co) => {
        if (co.topics?.length) {
            return buildHrefMap(co.topics, duplicates);
        } else {
            const isDuplicate = duplicates.has(co.lcmsXmlGUID);
            const key = isDuplicate
                ? `${co.id}-${co.lcmsXmlGUID}`
                : co.lcmsXmlGUID;
            const value = isDuplicate ? `${co.id}-${co.name}` : co.name;
            hrefArray.push(value);
            return new Map([
                [key, `#/${co.lcmsXmlGUID}/${encodeURIComponent(value)}`],
            ]);
        }
    });
    return hrefs.reduce(
        (map1, map2) =>
            new Map([
                ...Array.from(map1.entries()),
                ...Array.from(map2.entries()),
            ]),
        new Map([])
    );
};

/**
 * Strips the numeric ID prefix added to duplicate topic names for URL disambiguation.
 * When a lcmsXmlGUID appears more than once in a document, the href encodes the
 * topic name as `{id}-{name}` (e.g. "1198949-Maintenance preprocedure") so the
 * router can tell the copies apart. Only strips when the topic is a known duplicate
 * (i.e. the GUID is in the duplicates set) to avoid corrupting legitimate topic
 * names that start with digits (e.g. "3-Axis Calibration", "2024-Overview").
 */
export const stripDuplicatePrefixIfDuplicate = (
    topicName: string,
    isDuplicate: boolean
): string => {
    if (!isDuplicate) return topicName;
    return topicName.replace(/^\d+-/, '');
};

export const buildOrderedSubtopicList = (
    articleData: ArticleDetails
): { subtopicIds: string[]; duplicateSubtopics: string[] } => {
    const subtopicIds: string[] = [];
    const duplicateSubtopics: string[] = [];
    if (articleData.contentObjects.lessons?.length) {
        const dataContentObjects = articleData.contentObjects.lessons;

        const addTopicIdsToListInOrder = (cos: ContentObjects[]) => {
            cos.forEach((co) => {
                if (co.topics?.length) {
                    addTopicIdsToListInOrder(co.topics);
                } else {
                    duplicateSubtopics.push(`${co.id}-${co.lcmsXmlGUID}`);
                    subtopicIds.push(co.lcmsXmlGUID);
                }
            });
        };

        addTopicIdsToListInOrder(dataContentObjects);
    }

    return { subtopicIds, duplicateSubtopics };
};

export const generateFilter = (navItem: NavGraphItem): HiearchyFilterItem => {
    const childrenItems = Object.values(navItem.children || {})
        .filter((child) => {
            return (
                Object.keys(child.children || {}).length > 0 ||
                Object.keys(child.documents || {}).length > 0
            );
        })
        .sort((child1, child2) => {
            const child1NavOrder =
                child1.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;
            const child2NavOrder =
                child2.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;

            if (child1NavOrder && child2NavOrder) {
                if (child1NavOrder === child2NavOrder)
                    return child1.name.localeCompare(child2.name);
                return parseInt(child1NavOrder) < parseInt(child2NavOrder)
                    ? -1
                    : 1;
            } else if (child1NavOrder || child2NavOrder) {
                return child1NavOrder ? -1 : 1;
            }
            return child1.name.localeCompare(child2.name);
        })
        .map((item) => {
            const path = item.pathSegments
                ? item.pathSegments.map((seg) => seg.id).join('/')
                : item.path;

            return {
                title: item.name || '',
                path,
                dateUpdated: item.dateUpdated || '',
                description: item.description || '',
                image: item.pictureUrl || '',
                isDynamic: !!item.documentId,
            };
        });

    const documentItems = Object.values(navItem.documents || {})
        .sort((doc1, doc2) => {
            const doc1NavOrder =
                doc1.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;
            const doc2NavOrder =
                doc2.customAttributes
                    ?.find((attr) => attr.id === 19)
                    ?.value.replace(/[^0-9.]/g, '') || undefined;

            if (doc1NavOrder && doc2NavOrder) {
                if (doc1NavOrder === doc2NavOrder)
                    return doc1.name.localeCompare(doc2.name);
                return parseInt(doc1NavOrder) < parseInt(doc2NavOrder) ? -1 : 1;
            } else if (doc1NavOrder || doc2NavOrder) {
                return doc1NavOrder ? -1 : 1;
            }
            return doc1.name.localeCompare(doc2.name);
        })
        .map((document) => {
            return {
                title: document.latestVersion?.fileName || document.name || '',
                documentId: document.id,
                versionId: document.latestVersion.id,
                dateUpdated: document.dateUpdated || '',
                externalLinkUrl: document.externalLinkUrl,
                description: document.description || '',
                image: document.pictureUrl || '',
                isDynamic: document.isDynamic,
            };
        });

    return {
        title: navItem.name,
        itemSelected: null,
        items: [...childrenItems, ...documentItems],
    };
};

export const getItemByPath = (path: string, filteredGraph: NavGraph) => {
    const pathIds = path.split('/').filter(Boolean);
    if (pathIds[0] === String(NAV_GRAPH_ROOT_NODE_ID)) {
        pathIds.shift();
    }

    let current: NavGraphItem | undefined;
    pathIds.forEach((id, index) => {
        current = index === 0 ? filteredGraph[id] : current?.children?.[id];
    });
    return current as NavGraphItem;
};

export const makeInitialFilter = (
    filteredGraph: NavGraph
): HiearchyFilterItem[] => {
    const children = Object.values(filteredGraph);
    children.sort((child1, child2) => {
        const child1NavOrder =
            child1.customAttributes
                ?.find((attr) => attr.id === 19)
                ?.value.replace(/[^0-9.]/g, '') || undefined;
        const child2NavOrder =
            child2.customAttributes
                ?.find((attr) => attr.id === 19)
                ?.value.replace(/[^0-9.]/g, '') || undefined;

        if (child1NavOrder && child2NavOrder) {
            if (child1NavOrder === child2NavOrder)
                return child1.name.localeCompare(child2.name);
            return parseInt(child1NavOrder) < parseInt(child2NavOrder) ? -1 : 1;
        } else if (child1NavOrder || child2NavOrder) {
            return child1NavOrder ? -1 : 1;
        }
        return child1.name.localeCompare(child2.name);
    });

    return [
        {
            title: 'Home',
            itemSelected: null,
            items: children.map((child) => {
                const path = child.pathSegments
                    ? child.pathSegments.map((seg) => seg.id).join('/')
                    : child.path;

                return {
                    title: child?.name || '',
                    path,
                    description: child?.description || '',
                    image: child?.pictureUrl || '',
                };
            }),
        },
    ];
};
